import type { PropertyValues } from 'lit'
import { html, isServer, LitElement } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { createRef, ref } from 'lit/directives/ref.js'
import style from './Truncate.css'
import '../tooltip/Tooltip.js'

let counter = 0

const ELLIPSIS = '…'

/**
 * Truncate clips text with a single- or multi-line ellipsis and reveals a tooltip with the full text when the content overflows.
 *
 * @status ready
 * @category content
 * @slot - The full text content. Plain text is recommended; rich markup is
 *   not supported (only `textContent` is read for truncation).
 */
@customElement('nord-truncate')
export default class Truncate extends LitElement {
  static styles = style

  private spanRef = createRef<HTMLSpanElement>()
  private observer?: ResizeObserver
  private contentObserver?: MutationObserver
  private tooltipId = `n-truncate-${++counter}`
  // The tooltip lives in document.body (not in shadow DOM) so its
  // root-level mouseover listener can see hovers on the trigger element,
  // and so aria-describedby can resolve across the same document tree.
  private tooltipElement?: HTMLElement
  // The element that actually receives pointer events (often this host, but
  // when slotted into nord-button — which sets `::slotted(*) { pointer-events:
  // none }` — it resolves to the nord-button itself). aria-describedby is set
  // on this element so the tooltip's `closest()` lookup succeeds.
  private currentTrigger: Element | null = null

  @state() private isTruncated = false
  @state() private text = ''

  /**
   * Position of the tooltip relative to the truncated text. Mirrors `nord-tooltip`.
   */
  @property({ reflect: true })
  position: 'block-end' | 'block-start' | 'inline-start' | 'inline-end' = 'block-start'

  /**
   * Delay in milliseconds before the tooltip opens.
   */
  @property({ reflect: true, type: Number })
  delay = 500

  /**
   * Distance in pixels between the tooltip and the trigger along the main axis.
   * Mirrors `nord-tooltip`'s `side-offset` and is forwarded to the portaled
   * tooltip. Defaults to `8`.
   */
  @property({ reflect: true, type: Number, attribute: 'side-offset' })
  sideOffset = 8

  /**
   * Offset in pixels along the tooltip's alignment axis. Mirrors
   * `nord-tooltip`'s `align-offset` and is forwarded to the portaled tooltip.
   * Defaults to `0`.
   */
  @property({ reflect: true, type: Number, attribute: 'align-offset' })
  alignOffset = 0

  /**
   * Maximum number of lines before truncating. Defaults to `1` (single line).
   * Values `>= 2` allow the text to wrap up to that many lines before the
   * ellipsis kicks in.
   */
  @property({ type: Number, attribute: 'line-clamp' })
  lineClamp = 1

  /**
   * When set, the component still truncates the text but does not render
   * a hover tooltip with the full content. The `truncated` attribute is
   * still reflected so consumers can style it.
   */
  @property({ type: Boolean, reflect: true, attribute: 'no-tooltip' })
  noTooltip = false

  connectedCallback() {
    super.connectedCallback()

    if (isServer)
      return

    this.observer = new ResizeObserver(() => {
      // Defer to next frame so the browser has finished the layout pass
      // triggered by the resize before we read geometry.
      requestAnimationFrame(() => this.measure())
    })
    // Catch content changes the ResizeObserver can't see: text-only mutations
    // via reactive bindings (Lit/Vue/React often mutate characterData), or
    // children being replaced. Without this, swapping the label to a
    // longer/shorter string after first render would never re-evaluate
    // truncation.
    this.contentObserver = new MutationObserver(() => {
      this.captureText()
      this.measure()
    })
    this.contentObserver.observe(this, {
      characterData: true,
      childList: true,
      subtree: true,
    })
    this.captureText()
  }

  protected firstUpdated() {
    const span = this.spanRef.value
    if (span && this.observer) {
      this.observer.observe(span)
    }
  }

  protected updated() {
    this.syncTooltip()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.observer?.disconnect()
    this.observer = undefined
    this.contentObserver?.disconnect()
    this.contentObserver = undefined
    this.tooltipElement?.remove()
    this.tooltipElement = undefined
    this.clearTrigger()
  }

  private captureText() {
    const next = (this.textContent ?? '').trim()
    if (next !== this.text) {
      this.text = next
    }
  }

  /**
   * Measure overflow and, if needed, binary-search the longest prefix of the
   * full text that fits in the available space, then commit `<prefix>…` as
   * the visible text. Same algorithm as Ant Design Pro's `Ellipsis`.
   */
  private measure() {
    const span = this.spanRef.value
    if (!span)
      return

    const fullText = this.text
    if (!fullText) {
      span.textContent = ''
      this.isTruncated = false
      return
    }

    // Reset to full text and check whether it already fits.
    span.textContent = fullText
    if (this.fits(span)) {
      this.isTruncated = false
      return
    }

    // Binary search: largest prefix length such that `prefix + …` fits.
    let lo = 0
    let hi = fullText.length
    while (lo < hi) {
      const mid = Math.ceil((lo + hi) / 2)
      span.textContent = trimEnd(fullText.slice(0, mid)) + ELLIPSIS
      if (this.fits(span)) {
        lo = mid
      }
      else {
        hi = mid - 1
      }
    }

    span.textContent = trimEnd(fullText.slice(0, lo)) + ELLIPSIS
    this.isTruncated = true
  }

  private fits(span: HTMLElement): boolean {
    if (this.lineClamp > 1) {
      return span.scrollHeight <= span.clientHeight
    }
    return span.scrollWidth <= span.clientWidth
  }

  /**
   * Walk up the DOM until we find an ancestor that actually receives pointer
   * events. Needed because `nord-button` (and similar) set
   * `::slotted(*) { pointer-events: none }`, so this host can never see hover.
   * Falls back to this host if no such ancestor is found.
   */
  private findTriggerElement(): Element {
    // pointer-events walk from the host itself so a standalone truncate
    // (default pointer-events: auto) is correctly returned as the trigger.
    // eslint-disable-next-line ts/no-this-alias -- intentional: start the
    let element: Element | null = this
    while (element && element !== document.body && element !== document.documentElement) {
      if (getComputedStyle(element).pointerEvents !== 'none') {
        return element
      }
      element = element.parentElement
    }
    return this
  }

  private addDescribedById(element: Element, id: string) {
    const existing = element.getAttribute('aria-describedby')
    const ids = existing ? existing.split(/\s+/).filter(Boolean) : []
    if (!ids.includes(id)) {
      ids.push(id)
      element.setAttribute('aria-describedby', ids.join(' '))
    }
  }

  private removeDescribedById(element: Element, id: string) {
    const existing = element.getAttribute('aria-describedby')
    if (!existing)
      return
    const ids = existing.split(/\s+/).filter(Boolean).filter(x => x !== id)
    if (ids.length) {
      element.setAttribute('aria-describedby', ids.join(' '))
    }
    else {
      element.removeAttribute('aria-describedby')
    }
  }

  private clearTrigger() {
    if (this.currentTrigger) {
      this.removeDescribedById(this.currentTrigger, this.tooltipId)
      this.currentTrigger = null
    }
  }

  private syncTooltip() {
    if (!this.isTruncated || !this.text) {
      this.removeAttribute('truncated')
      this.tooltipElement?.remove()
      this.tooltipElement = undefined
      this.clearTrigger()
      return
    }

    this.setAttribute('truncated', '')

    if (this.noTooltip) {
      this.tooltipElement?.remove()
      this.tooltipElement = undefined
      this.clearTrigger()
      return
    }

    if (!this.tooltipElement) {
      this.tooltipElement = document.createElement('nord-tooltip')
      this.tooltipElement.id = this.tooltipId
      document.body.appendChild(this.tooltipElement)
    }
    this.tooltipElement.setAttribute('position', this.position)
    this.tooltipElement.setAttribute('delay', String(this.delay))
    // The tooltip is portaled to document.body, so forward the offsets onto it
    // directly rather than relying on inheritance from this host.
    this.tooltipElement.setAttribute('side-offset', String(this.sideOffset))
    this.tooltipElement.setAttribute('align-offset', String(this.alignOffset))
    this.tooltipElement.textContent = this.text

    const trigger = this.findTriggerElement()
    if (trigger !== this.currentTrigger) {
      if (this.currentTrigger) {
        this.removeDescribedById(this.currentTrigger, this.tooltipId)
      }
      this.currentTrigger = trigger
    }
    this.addDescribedById(trigger, this.tooltipId)
  }

  protected willUpdate(changed: PropertyValues) {
    if (changed.has('lineClamp')) {
      if (this.lineClamp > 1) {
        this.setAttribute('line-clamp', String(this.lineClamp))
        this.style.setProperty('--_n-truncate-line-clamp', String(this.lineClamp))
      }
      else {
        this.removeAttribute('line-clamp')
        this.style.removeProperty('--_n-truncate-line-clamp')
      }
    }
  }

  render() {
    // The visible truncated text lives in a dedicated shadow-DOM span whose
    // `textContent` is managed imperatively in `measure()` (binary-search
    // updates would otherwise destroy Lit's ChildPart markers). The slot,
    // hidden visually, keeps the full text in light DOM so that assistive
    // tech and copy/paste of the host element still see the complete content.
    return html`
      <span ${ref(this.spanRef)} class="n-truncate"></span>
      <slot class="n-truncate-source" aria-hidden="true"></slot>
    `
  }
}

function trimEnd(input: string): string {
  return input.replace(/[\s.,;:!?-]+$/u, '')
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-truncate': Truncate
  }
}
